/** Every action ends with a clear next step. */

export function withNextStep(body: string, next: string): string {
  const n = next.startsWith("Tu siguiente paso") ? next : `Tu siguiente paso es ${next}`;
  if (body.includes("Tu siguiente paso")) return body;
  return `${body.trim()}\n\n${n}`;
}

export function nowYouCan(options: string[]): string {
  const lines = options.map((o) => `- ${o}`);
  return ["Ahora podés:", ...lines].join("\n");
}

export const DEFAULT_NEXT_STEPS = {
  afterProposal: "revisar la propuesta y aprobar o cancelar",
  afterPlan: "revisar el plan de trabajo y sus acciones",
  afterEmpty: "pedir ayuda a Renata o crear el primer elemento",
  afterCapture: "revisar lo capturado y aprobar los cambios",
  afterLearning: "revisar la cola de aprendizajes",
} as const;

export interface ApprovalNextStep {
  message: string;
  ctaLabel: string;
  href: string;
}

/** Resolve a concrete post-approve next step from proposal metadata. */
export function resolveApprovalNextStep(input: {
  skillId?: string | null;
  action?: string | null;
  params?: Record<string, unknown> | null;
}): ApprovalNextStep {
  const skill = String(input.skillId ?? "").toLowerCase();
  const action = String(input.action ?? "").toLowerCase();
  const blob = `${skill} ${action} ${JSON.stringify(input.params ?? {})}`.toLowerCase();

  if (/mission|plan_de_trabajo|create_mission/.test(blob)) {
    return {
      message: "Listo. Renata armó un plan de trabajo. Podés seguir el progreso y las acciones desde Planes de trabajo.",
      ctaLabel: "Ver planes de trabajo",
      href: "/app/renata/missions",
    };
  }
  if (/task|accion|create_task|propose_task/.test(blob)) {
    return {
      message: "Listo. Se crearon o actualizaron acciones concretas. Revisá el estado y el origen de cada una.",
      ctaLabel: "Ver acciones",
      href: "/app/renata/tasks",
    };
  }
  if (/goal|objetivo/.test(blob)) {
    return {
      message: "Listo. El objetivo quedó registrado. El siguiente paso es armar un plan para alcanzarlo.",
      ctaLabel: "Ver objetivos",
      href: "/app/renata/goals",
    };
  }
  if (/playbook|guia|guía/.test(blob)) {
    return {
      message: "Listo. La guía de trabajo quedó disponible. Revisá los pasos y adaptarlos a tu negocio.",
      ctaLabel: "Ver guías",
      href: "/app/renata/playbooks",
    };
  }
  if (/learning|aprendizaje/.test(blob)) {
    return {
      message: "Listo. El aprendizaje quedó en cola. Nada se aplica solo: revisá y aprobá lo que quieras conservar.",
      ctaLabel: "Ver aprendizajes",
      href: "/app/renata/learning",
    };
  }
  if (/knowledge|biblioteca|document|ingest/.test(blob)) {
    return {
      message: "Listo. La información quedó en la biblioteca. Renata puede citarla en futuras respuestas.",
      ctaLabel: "Abrir biblioteca",
      href: "/app/renata/knowledge",
    };
  }
  if (/automat|workflow|rule/.test(blob)) {
    return {
      message: "Listo. Revisá la automatización: solo propone acciones gobernadas; no ejecuta sola si está apagada.",
      ctaLabel: "Ver automatizaciones",
      href: "/app/renata/automatizaciones",
    };
  }
  if (/push|campaign|aviso/.test(blob)) {
    return {
      message: "Listo. Revisá el aviso push antes de enviarlo: audiencia, mensaje y alcance estimado.",
      ctaLabel: "Ver avisos push",
      href: "/app/renata/push",
    };
  }
  if (/product|precio|stock|catalog/.test(blob)) {
    return {
      message: "Listo. El cambio quedó aplicado. Revisá el producto o pedile a Renata el siguiente ajuste.",
      ctaLabel: "Ir a productos",
      href: "/app/productos",
    };
  }
  if (/insight|oportunidad/.test(blob)) {
    return {
      message: "Listo. Podés pedir un plan de trabajo a partir de esta oportunidad.",
      ctaLabel: "Ver oportunidades",
      href: "/app/renata/insights",
    };
  }

  return {
    message: "Listo. Renata aplicó lo aprobado. Revisá el resultado o pedile el siguiente paso.",
    ctaLabel: "Volver a Renata",
    href: "/app/renata",
  };
}
